import dayjs from "dayjs";
import * as XLSX from "xlsx";

import { BUDGET_MONTH_HEADERS } from "~/lib/contabilidad/generateBudgetTemplate";
import { BUDGET_STATUS_LABELS } from "~/types/contabilidad";

import type { Budget } from "~/types/contabilidad";

export interface BudgetExportRow {
  code: string;
  name: string;
  amounts: number[]; // 12 values, Ene..Dic
}

/**
 * Export the budget grid to Excel in the same shape as the import template
 * (Codigo | Cuenta | Ene..Dic, plus a Total column the parser ignores),
 * so the file round-trips as an import.
 */
export function exportBudgetToExcel(
  budget: Budget,
  companyName: string,
  rows: BudgetExportRow[]
): void {
  const wb = XLSX.utils.book_new();

  // Info sheet (kept out of the data sheet so the export re-imports cleanly)
  const info = [
    ["PRESUPUESTO"],
    [],
    ["Empresa:", companyName],
    ["Año:", budget.fiscal_year],
    ["Nombre:", budget.name],
    ["Estado:", BUDGET_STATUS_LABELS[budget.status]],
    ["Fecha de generación:", dayjs().format("DD/MM/YYYY HH:mm")],
  ];
  const infoSheet = XLSX.utils.aoa_to_sheet(info);
  infoSheet["!cols"] = [{ wch: 25 }, { wch: 40 }];
  XLSX.utils.book_append_sheet(wb, infoSheet, "Información");

  const data: (string | number)[][] = [
    ["Codigo", "Cuenta", ...BUDGET_MONTH_HEADERS, "Total"],
    ...rows.map((row) => [
      row.code,
      row.name,
      ...row.amounts.map((amount) => Math.round(amount)),
      Math.round(row.amounts.reduce((sum, amount) => sum + amount, 0)),
    ]),
  ];

  const dataSheet = XLSX.utils.aoa_to_sheet(data);
  dataSheet["!cols"] = [
    { wch: 15 },
    { wch: 40 },
    ...BUDGET_MONTH_HEADERS.map(() => ({ wch: 14 })),
    { wch: 16 },
  ];
  XLSX.utils.book_append_sheet(wb, dataSheet, "Presupuesto");

  const filename = `presupuesto_${companyName
    .toLowerCase()
    .replace(/\s+/g, "_")}_${budget.fiscal_year}_${dayjs().format("YYYY-MM-DD")}.xlsx`;

  XLSX.writeFile(wb, filename);
}
